import jsPDF from 'jspdf';

/**
 * Generate Professional PDF Report for Car Scan AI
 * @param {Object} reservationData - The reservation data from GraphQL
 * @param {Object} reportData - The parsed reportText data
 * @returns {jsPDF} PDF document object
 */
export const generateCarScanAIPDF = (reservationData, reportData) => {
  // Create new PDF document with landscape orientation for better layout
  const doc = new jsPDF('p', 'mm', 'a4');
  
  // Set document properties
  doc.setProperties({
    title: 'Car Scan AI Report',
    subject: `Professional Report for ${reservationData.listTitle}`,
    author: 'Motostream Admin System',
    creator: 'Car Scan AI Professional Report Generator'
  });

  // Page dimensions
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const contentWidth = pageWidth - (2 * margin);

  // Add simple header
  doc.setFillColor(51, 51, 51); // Simple dark gray header
  doc.rect(0, 0, pageWidth, 30, 'F');
  
  // Add simple logo
  doc.setFillColor(255, 255, 255);
  doc.circle(25, 15, 6, 'F');
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(12);
  doc.text('CS', 22, 19);
  
  // Main title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Car Scan AI Report', pageWidth/2, 20, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth/2, 26, { align: 'center' });

  let yPosition = 45;

  // Add car information card
  yPosition = addInfoCard(doc, 'Vehicle Information', [
    { label: 'Car Model', value: String(reservationData.listTitle || 'N/A') },
    { label: 'Report Status', value: String(reservationData.report || 'N/A'), highlight: true },
    { label: 'Reservation State', value: String(reservationData.reservationState || 'N/A') },
    { label: 'Check In', value: String(formatDate(reservationData.checkIn) || 'N/A') },
    { label: 'Check Out', value: String(formatDate(reservationData.checkOut) || 'N/A') }
  ], yPosition, pageWidth, margin);

  // Add report details card
  if (reportData && typeof reportData === 'object') {
    yPosition = addInfoCard(doc, 'Technical Specifications', [
      { label: 'Car Year', value: String(reportData.car_year || 'N/A') },
      { label: 'Car Color', value: String(reportData.car_color || 'N/A') },
      { label: 'Car Model', value: String(reportData.car_model || 'N/A') },
      { label: 'Total Parts Analyzed', value: String(reportData.total_parts_analyzed || 'N/A'), highlight: true },
      { label: 'Request ID', value: String(reportData.request_id || 'N/A') }
    ], yPosition + 15, pageWidth, margin);

    // Add overall summary with professional styling
    if (reportData.overall_summary) {
      yPosition = addSummarySection(doc, 'Executive Summary', reportData.overall_summary, yPosition + 15, pageWidth, margin);
    }

    // Add part analysis with enhanced styling
    if (reportData.part_reports && Array.isArray(reportData.part_reports)) {
      yPosition = addPartAnalysisSection(doc, reportData.part_reports, yPosition + 15, pageWidth, margin);
    }
  }

  // Add professional footer
  addProfessionalFooter(doc, pageWidth, pageHeight, margin);
  
  // Ensure all content is properly rendered
  doc.setPage(doc.internal.getNumberOfPages());
  
  return doc;
};

/**
 * Add a professional information card
 */
function addInfoCard(doc, title, data, startY, pageWidth, margin) {
  const cardHeight = 35;
  const cardWidth = pageWidth - (2 * margin);
  
  // Simple card background
  doc.setFillColor(248, 249, 250); // Light gray background
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.rect(margin, startY, cardWidth, cardHeight, 'FD');
  
  // Simple card title
  doc.setFillColor(70, 70, 70); // Dark gray
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.rect(margin, startY, cardWidth, 10, 'F');
  doc.text(title, margin + 6, startY + 7);
  
  // Data rows
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  let yPos = startY + 15;
  data.forEach((item, index) => {
    const rowY = yPos + (index * 3);
    
    // Label
    doc.setFont('helvetica', 'bold');
    doc.text(`${item.label}:`, margin + 6, rowY);
    
    // Value
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const valueString = String(item.value || 'N/A');
    doc.text(valueString, margin + 40, rowY);
    
    // Reset color
    doc.setTextColor(50, 50, 50);
  });
  
  return startY + cardHeight;
}

/**
 * Add summary section with professional styling
 */
function addSummarySection(doc, title, summary, startY, pageWidth, margin) {
  const cardHeight = 25;
  const cardWidth = pageWidth - (2 * margin);
  
  // Simple card background
  doc.setFillColor(252, 252, 252); // Very light gray background
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.rect(margin, startY, cardWidth, cardHeight, 'FD');
  
  // Simple title
  doc.setFillColor(70, 70, 70); // Dark gray
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.rect(margin, startY, cardWidth, 10, 'F');
  doc.text(title, margin + 6, startY + 7);
  
  // Summary text
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  // Clean and format summary text
  let cleanSummary = String(summary || '')
    .replace(/FOR/g, 'for')
    .replace(/OUT/g, 'out of')
    .replace(/severely damaged parts/g, 'severely damaged parts');
  
  const summaryLines = doc.splitTextToSize(cleanSummary, cardWidth - 16);
  doc.text(summaryLines, margin + 6, startY + 18);
  
  return startY + cardHeight;
}

/**
 * Add part analysis section with enhanced styling
 */
function addPartAnalysisSection(doc, partReports, startY, pageWidth, margin) {
  let yPosition = startY;
  
  // Section header
  doc.setFillColor(70, 70, 70); // Simple dark gray header
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.rect(margin, yPosition, pageWidth - (2 * margin), 10, 'F');
  doc.text('Detailed Part Analysis', margin + 6, yPosition + 7);
  yPosition += 20;
  
  partReports.forEach((part, index) => {
    // Check if we need a new page before adding content
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }
    
      // Simple part header card
  doc.setFillColor(100, 100, 100); // Simple gray
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(0.3);
  doc.rect(margin, yPosition, pageWidth - (2 * margin), 12, 'FD');
    
      // Part title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`${index + 1}. ${part.part_name || 'Unknown'} - ${part.severity || 'Unknown'} Damage`, margin + 6, yPosition + 7);
  doc.text(`Timestamp: ${part.timestamp || 'Unknown'}`, margin + 6, yPosition + 10);
  yPosition += 18;
    
    // Damage analysis
    if (part.damage_analysis) {
      yPosition = addAnalysisSubsection(doc, 'Damage Analysis', part.damage_analysis, yPosition, pageWidth, margin);
    }
    
    // Recommendations
    if (part.recommendations) {
      yPosition = addAnalysisSubsection(doc, 'Recommendations', part.recommendations, yPosition, pageWidth, margin);
    }
    
    // Photo URLs
    if (part.before_photo_url || part.after_photo_url) {
      yPosition = addPhotoUrls(doc, part, yPosition, pageWidth, margin);
    }
    
    yPosition += 10;
  });
  
  return yPosition;
}

/**
 * Add analysis subsection
 */
function addAnalysisSubsection(doc, title, content, startY, pageWidth, margin) {
  const cardWidth = pageWidth - (2 * margin);
  
  // Simple subsection header
  doc.setFillColor(120, 120, 120); // Simple gray
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.rect(margin, startY, cardWidth, 7, 'F');
  doc.text(title, margin + 5, startY + 5);
  startY += 12;
  
  // Content
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  // Clean content and ensure it's a string
  let cleanContent = String(content || '')
    .replace(/### /g, '')
    .replace(/\*\*/g, '')
    .replace(/\\n/g, ' ')
    .replace(/Before Image:/g, 'Before Image:')
    .replace(/After Image:/g, 'After Image:')
    .replace(/Visible Damage:/g, 'Visible Damage:')
    .replace(/Severity:/g, 'Severity:')
    .replace(/Recommendations:/g, 'Recommendations:');
  
  const contentLines = doc.splitTextToSize(cleanContent, cardWidth - 12);
  doc.text(contentLines, margin + 5, startY);
  
  // Calculate proper height for content
  const contentHeight = Math.max(contentLines.length * 3, 8);
  return startY + contentHeight + 8;
}

/**
 * Add photo URLs section
 */
function addPhotoUrls(doc, part, startY, pageWidth, margin) {
  const cardWidth = pageWidth - (2 * margin);
  
  // Simple photo URLs header
  doc.setFillColor(120, 120, 120); // Simple gray
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.rect(margin, startY, cardWidth, 7, 'F');
  doc.text('Photo References', margin + 5, startY + 5);
  startY += 12;
  
  // URLs
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  
  if (part.before_photo_url) {
    doc.text(`Before: ${part.before_photo_url}`, margin + 5, startY);
    startY += 4;
  }
  
  if (part.after_photo_url) {
    doc.text(`After: ${part.after_photo_url}`, margin + 5, startY);
    startY += 4;
  }
  
  return startY + 8;
}



/**
 * Format date for better readability
 */
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Add professional footer
 */
function addProfessionalFooter(doc, pageWidth, pageHeight, margin) {
  const pageCount = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Footer line
    doc.setDrawColor(222, 226, 230);
    doc.setLineWidth(0.5);
    doc.line(margin, pageHeight - 25, pageWidth - margin, pageHeight - 25);
    
    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(108, 117, 125);
    doc.setFont('helvetica', 'normal');
    
    // Left side
    doc.text('Motostream Car Scan AI System', margin, pageHeight - 15);
    
    // Center
    doc.text(`Page ${i} of ${pageCount}`, pageWidth/2, pageHeight - 15, { align: 'center' });
    
    // Right side
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - margin, pageHeight - 15, { align: 'right' });
  }
}

/**
 * Download PDF Report
 * @param {Object} reservationData - The reservation data from GraphQL
 * @param {Object} reportData - The parsed reportText data
 * @param {string} filename - Optional filename for download
 */
export const downloadCarScanAIPDF = (reservationData, reportData, filename = null) => {
  try {
    const doc = generateCarScanAIPDF(reservationData, reportData);
    
    // Generate filename if not provided
    if (!filename) {
      const carName = reservationData.listTitle || 'Car';
      const date = new Date().toISOString().split('T')[0];
      filename = `CarScanAI_Professional_Report_${carName}_${date}.pdf`;
    }
    
    // Save the PDF
    doc.save(filename);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    return false;
  }
};

/**
 * Open PDF Report in new tab
 * @param {Object} reservationData - The reservation data from GraphQL
 * @param {Object} reportData - The parsed reportText data
 */
export const openCarScanAIPDF = (reservationData, reportData) => {
  try {
    const doc = generateCarScanAIPDF(reservationData, reportData);
    
    // Generate PDF blob for better browser compatibility
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    // Open PDF in new tab with proper URL
    const newWindow = window.open(pdfUrl, '_blank');
    
    // Clean up the blob URL after a delay
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 1000);
    
    return true;
  } catch (error) {
    console.error('Error opening PDF:', error);
    return false;
  }
};
